import {
  CommonDecisionTreeNodeType,
  DecisionTreeData,
  DecisionTreeNodeEdgeType,
  MacroDecisionTreeNode,
  MacroDecisionTreeNodeData,
  MacroDecisionTreeNodeType,
} from '@frontapp/workflows/decision_trees';
import {SomeMacroAction, SomeMacroCondition} from '@frontapp/workflows/macros';
import {CompoundConditionOperator, RuleActionType} from '@frontapp/workflows/rules';

import {makeEdge} from '../../../../libs/shared/core/src/helpers/models/decisionTreeHelpers';
import {makeMacroFlowBuilderDefaultNodeData} from '../../../../libs/shared/core/src/helpers/models/macroFlowBuilderHelpers';
import {
  DecisionTreeEdgeTypesEnum,
  DecisionTreeNode,
  DecisionTreeNodeTypesEnum,
} from '../../../../libs/shared/core/src/helpers/types/decisionTreeTypes';
import {
  ClientMacroDecisionTreeNodeAction,
  ClientMacroDecisionTreeNodeData,
  MacroFlowBuilderNodeData,
} from '../../../../libs/shared/core/src/helpers/types/macroFlowBuilderTypes';
import {assertUnreachable} from '../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {decorateWithClientId} from '../../../../libs/shared/core/src/models/clientIdModel';
import {withDecisionTreeLayout} from '../../../../libs/shared/ui/src/components/decisionTrees/decisionTreeBuilder/decisionTreeLayout';
import {TreeData} from '../../../../libs/shared/ui/src/components/decisionTrees/decisionTreeBuilder/types';
import {macroFlowBuilderLayoutOptions} from './constants';

/*
 * Helpers.
 */

export function makeDefaultMacroBuilderTreeState(
  actions?: ReadonlyArray<ClientMacroDecisionTreeNodeAction>,
): TreeData<MacroFlowBuilderNodeData> {
  return withMacroDecisionTreeLayout({
    nodes: [
      {
        type: DecisionTreeNodeTypesEnum.WORKFLOW,
        position: {x: 0, y: 0},
        id: '1',
        data: {type: MacroDecisionTreeNodeType.START},
      },
      {
        type: DecisionTreeNodeTypesEnum.WORKFLOW,
        position: {
          x: 108.33333333333333,
          y: 0,
        },
        id: '2',
        data: {
          type: MacroDecisionTreeNodeType.INTRO,
        },
      },
      {
        type: DecisionTreeNodeEdgeType.PLACEHOLDER,
        position: {
          x: 433.3333333333333,
          y: 0,
        },
        id: '3',
        data: actions
          ? {
              type: CommonDecisionTreeNodeType.ACTION,
              actions,
            }
          : makeMacroFlowBuilderDefaultNodeData(CommonDecisionTreeNodeType.ACTION),
      },
    ],
    edges: [
      makeEdge('1', '2', DecisionTreeEdgeTypesEnum.WORKFLOW),
      makeEdge('2', '3', DecisionTreeEdgeTypesEnum.WORKFLOW),
    ],
  });
}

export function withMacroDecisionTreeLayout(
  tree: TreeData<MacroFlowBuilderNodeData>,
): TreeData<MacroFlowBuilderNodeData> {
  return withDecisionTreeLayout(tree, macroFlowBuilderLayoutOptions);
}

function decorateMacroDecisionTreeNodeWithClientId(
  node: MacroDecisionTreeNode,
): DecisionTreeNode<ClientMacroDecisionTreeNodeData> {
  switch (node.data.type) {
    case MacroDecisionTreeNodeType.START:
      return {
        ...node,
        position: node.position ?? {x: 0, y: 0},
        data: {
          ...node.data,
        },
      };
    case MacroDecisionTreeNodeType.INTRO:
      return {
        ...node,
        position: node.position ?? {x: 0, y: 0},
        data: {
          ...node.data,
        },
      };
    case CommonDecisionTreeNodeType.BRANCH_BY_CONDITION:
      return {
        ...node,
        position: node.position ?? {x: 0, y: 0},
        data: {
          ...node.data,
          options: node.data.options.map((option) => {
            const [operator, ...conditions] = option.conditions;
            return {
              ...option,
              conditions: [
                operator,
                ...(conditions as ReadonlyArray<SomeMacroCondition>).map(decorateWithClientId),
              ],
            };
          }),
        },
      };
    case CommonDecisionTreeNodeType.ACTION:
      return {
        ...node,
        position: node.position ?? {x: 0, y: 0},
        data: {
          ...node.data,
          actions: (node.data.actions as ReadonlyArray<SomeMacroAction>).map(decorateWithClientId),
        },
      };
    case MacroDecisionTreeNodeType.COLLECT_INPUT:
      return node as DecisionTreeNode<ClientMacroDecisionTreeNodeData>;
    default:
      assertUnreachable(node.data);
      return node as DecisionTreeNode<ClientMacroDecisionTreeNodeData>;
  }
}

export function decorateMacroDecisionTreeWithClientId(
  decisionTree: DecisionTreeData<MacroDecisionTreeNodeData>,
): TreeData<MacroFlowBuilderNodeData> {
  return {
    edges: [...decisionTree.edges],
    nodes: decisionTree.nodes.map((node) => decorateMacroDecisionTreeNodeWithClientId(node)),
  };
}

function migrateMacroRuleOldAction(
  action: ClientMacroDecisionTreeNodeAction,
): ClientMacroDecisionTreeNodeAction {
  if (action.type === RuleActionType.UNTAG) {
    return {
      ...action,
      type: RuleActionType.REMOVE_TAGS,
      params: {
        tags: [action.params.tag],
      },
    };
  }
  return action;
}

function migrateMacroDecisionTreeNodeOldActions(
  node: DecisionTreeNode<ClientMacroDecisionTreeNodeData>,
): DecisionTreeNode<ClientMacroDecisionTreeNodeData> {
  if (node.data.type === CommonDecisionTreeNodeType.ACTION) {
    return {
      ...node,
      data: {
        ...node.data,
        actions: node.data.actions.map(migrateMacroRuleOldAction),
      },
    };
  }
  return node;
}

export function migrateMacroDecisionTreeOldActions(
  decisionTree: TreeData<MacroFlowBuilderNodeData>,
): TreeData<MacroFlowBuilderNodeData> {
  return {
    edges: decisionTree.edges,
    nodes: decisionTree.nodes.map(migrateMacroDecisionTreeNodeOldActions),
  };
}

type AndSimpleCompoundConditions<T> = [CompoundConditionOperator.AND, ...Array<T>];

export function mapMacroCompoundConditions<T, U>(
  compoundConditions: AndSimpleCompoundConditions<T>,
  mapFn: (value: T) => U,
): AndSimpleCompoundConditions<U> {
  const [operator, ...conditions] = compoundConditions;
  return [operator, ...conditions.map(mapFn)];
}

export function filterMacroCompoundConditions<T>(
  compoundConditions: AndSimpleCompoundConditions<T>,
  filterFn: (value: T) => boolean,
): AndSimpleCompoundConditions<T> {
  const [operator, ...conditions] = compoundConditions;
  return [operator, ...conditions.filter(filterFn)];
}

export function canCreateCollectInputNode(
  nodes: ReadonlyArray<{id: string; data: ClientMacroDecisionTreeNodeData}>,
  sourceNodeId: string,
) {
  const sourceNode = nodes.find((node) => node.id === sourceNodeId);
  if (!sourceNode) {
    return false;
  }

  // Don't allow two input capture nodes
  if (nodes.some((node) => node.data.type === MacroDecisionTreeNodeType.COLLECT_INPUT)) {
    return false;
  }

  // Only allow collect input nodes after start nodes.
  if (
    !(
      sourceNode.data.type === MacroDecisionTreeNodeType.START ||
      sourceNode.data.type === MacroDecisionTreeNodeType.INTRO
    )
  ) {
    return false;
  }

  return true;
}
